import html2canvas from 'html2canvas'
import moment from 'moment'
import React, { FC, useRef, useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { EuiSpacer, EuiText, EuiLoadingContent, EuiFlexGroup, EuiFlexItem, EuiButton, EuiTitle, EuiButtonIcon } from '@elastic/eui'

import { MasterCampaignPlan, MasterCampaignPlanStatus } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import {
  useGetMasterCampaignPlanMasterCampaignStubQuery,
  useGetMasterCampaignPlanMediaPlanSectionQuery,
  useUpdateMediaPlanBudgetSectionMutation,
  useUpdateMediaPlanCampaignDetailsTitleMutation,
  useHideMediaPlanSectionMutation,
  useAddMediaPlanCustomSectionMutation,
  useUpdateMediaPlanCustomSectionMutation,
  useDeleteMediaPlanCustomSectionMutation,
  useUpdateMediaPlanPageTitleMutation,
  useTransferMasterCampaignPlanToDspMutation
} from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import { CampaignCard } from 'features/planner/components/CampaignCard'
import { CampaignSpecificBillboardMap, CampaignSpecificBillboardMapRef, CampaignSpecificTargetedHouseholdMap, CampaignSpecificTargetedHouseholdMapRef } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/CampaignSpecificMaps'
import { calculateEndDate } from 'utils/MasterCampaignPlanUtils'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectEditMode } from '../masterCampaignPlanSlice'
import { selectMultiTabPdfGenerationState, setGanttChartImage } from '../pdfViewSlice'
import { getBudgetContent } from '../utils/planContentUtils'
import Budget from './Budget'
import { AddCustomSectionButton } from './ExecutiveSummary/AddCustomSectionButton'
import CampaignTimeline from './ExecutiveSummary/CampaignTimeline'
import EditCustomSectionFlyout from './ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from './ExecutiveSummary/EditPageTitleFlyout'
import MediaPlanPdf from './MediaPlanPdf'
import TabPageHeader from './TabPageHeader'
import { RenderCustomSections } from './shared/RenderCustomSections'

type SectionType = 'budget'
export type MediaPlanCustomSectionPosition = 'aboveCampaignTimeline' | 'aboveCampaignDetails'

export interface MediaPlanRef {
  getCampaignMapRefs: () => Map<string, CampaignSpecificTargetedHouseholdMapRef | CampaignSpecificBillboardMapRef>
}

export interface MediaPlanProps {
  masterCampaignPlan: MasterCampaignPlan
  onEditCampaignClick?: (builder: MasterCampaignPlanCampaignBuilder) => void
  onAddCampaignClick?: () => void
  onRemoveCampaignClick?: (builder: MasterCampaignPlanCampaignBuilder) => Promise<void>
  isRemovingCampaign?: boolean
  onAllClientPdfsClick?: () => void
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

const generateCampaignsHash = (campaigns: MasterCampaignPlanCampaignBuilder[]): string => {
  const campaignData = campaigns.map(c => ({
    id: c.id,
    name: c.name,
    startDate: c.startDate,
    intervalDuration: c.intervalDuration,
    intervalBudget: c.intervalBudget,
    campaignType: c.campaignType
  }))
  return JSON.stringify(campaignData)
}

const waitForMapReady = (checkIsReady: () => boolean, maxAttempts = 40, interval = 250): Promise<void> => {
  return new Promise((resolve, reject) => {
    let attempts = 0
    const checkReady = () => {
      if (checkIsReady()) {
        resolve()
      } else if (attempts >= maxAttempts) {
        resolve()
      } else {
        attempts++
        setTimeout(checkReady, interval)
      }
    }
    checkReady()
  })
}

const MediaPlan = forwardRef<MediaPlanRef, MediaPlanProps>(({ masterCampaignPlan, onEditCampaignClick, onAddCampaignClick, onRemoveCampaignClick, isRemovingCampaign = false, onAllClientPdfsClick, onAllStaffPdfsClick, staffPdfDisabledReason }, ref) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const ganttChartRef = useRef<HTMLDivElement>(null)
  const campaignMapRefs = useRef<Map<string, CampaignSpecificTargetedHouseholdMapRef | CampaignSpecificBillboardMapRef>>(new Map())

  const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectEditMode)
  const { isGenerating: isMultitabPdfGenerating } = useSelector((state: RootState) => selectMultiTabPdfGenerationState(state, masterCampaignPlan.id))
  const [transferToDspModalVisible, setTransferToDspModalVisible] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [campaignMapImages, setCampaignMapImages] = useState<Record<string, string | null>>({})

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<MediaPlanCustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: MediaPlanCustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: MediaPlanCustomSectionPosition; index: number } | null>(null)
  const [showBudgetSaveWarning, setShowBudgetSaveWarning] = useState(false)
  const [pendingBudgetSave, setPendingBudgetSave] = useState<{ title: string; content: string; showTitle: boolean } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)
  const [editingCampaignDetailsTitle, setEditingCampaignDetailsTitle] = useState(false)

  const { data: masterCampaignStub, isLoading: isMasterCampaignStubLoading } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlan.id })
  const { data: mediaPlanSection } = useGetMasterCampaignPlanMediaPlanSectionQuery({ masterCampaignPlanId: masterCampaignPlan.id })

  const [updateBudget, { isLoading: isUpdatingBudget }] = useUpdateMediaPlanBudgetSectionMutation()
  const [updateCampaignDetailsTitle, { isLoading: isUpdatingCampaignDetailsTitle }] = useUpdateMediaPlanCampaignDetailsTitleMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHideMediaPlanSectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddMediaPlanCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdateMediaPlanCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeleteMediaPlanCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdateMediaPlanPageTitleMutation()
  const [transferMasterCampaignPlanToDspMutation, transferMasterCampaignPlanToDspResult] = useTransferMasterCampaignPlanToDspMutation()

  const campaigns = masterCampaignStub?.campaignBuilders || []
  const planStartDate = moment(masterCampaignPlan.aiUsedStartDate)
  const planDurationMonths = masterCampaignPlan.aiUsedDurationInMonths
  const campaignsHash = generateCampaignsHash(campaigns)

  useImperativeHandle(ref, () => ({
    getCampaignMapRefs: () => campaignMapRefs.current
  }))

  const handleEditSection = (sectionType: SectionType) => {
    setEditingSectionType(sectionType)
  }

  const handleDeleteSection = (sectionType: SectionType) => {
    setDeletingSectionType(sectionType)
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    if (editingSectionType === 'budget') {
      setPendingBudgetSave({ title, content, showTitle })
      setShowBudgetSaveWarning(true)
      return
    }

    setEditingSectionType(null)
  }

  const confirmBudgetSave = async () => {
    if (!pendingBudgetSave) return

    const params = {
      masterCampaignPlanId: masterCampaignPlan.id,
      title: pendingBudgetSave.title,
      content: pendingBudgetSave.content,
      showTitle: pendingBudgetSave.showTitle
    }

    await updateBudget(params)

    setShowBudgetSaveWarning(false)
    setPendingBudgetSave(null)
    setEditingSectionType(null)
  }

  const cancelBudgetSave = () => {
    setShowBudgetSaveWarning(false)
    setPendingBudgetSave(null)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleCancelDelete = () => {
    if (!isHidingSection) {
      setDeletingSectionType(null)
    }
  }

  const handleAddCustomSection = (position: MediaPlanCustomSectionPosition) => {
    setAddingCustomSectionPosition(position)
  }

  const handleEditCustomSection = (position: MediaPlanCustomSectionPosition, index: number) => {
    setEditingCustomSection({ position, index })
  }

  const handleDeleteCustomSection = (position: MediaPlanCustomSectionPosition, index: number) => {
    setDeletingCustomSection({ position, index })
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = mediaPlanSection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = mediaPlanSection?.[editingCustomSection.position] || []
      await updateCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = mediaPlanSection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleCancelDeleteCustomSection = () => {
    if (!isDeletingCustomSection) {
      setDeletingCustomSection(null)
    }
  }

  const handleEditPageTitle = () => {
    setEditingPageTitle(true)
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const handleEditCampaignDetailsTitle = () => {
    setEditingCampaignDetailsTitle(true)
  }

  const handleSaveCampaignDetailsTitle = async (newTitle: string) => {
    await updateCampaignDetailsTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      campaignDetailsTitle: newTitle
    })
    setEditingCampaignDetailsTitle(false)
  }

  const isUpdatingAnySection = isUpdatingBudget || isAddingCustomSection || isUpdatingCustomSection

  const budgetHidden = mediaPlanSection?.budget?.hideAll === true

  const getDefaultTitle = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'budget':
        return 'Budget'
    }
  }

  const getDefaultShowTitle = (sectionType: SectionType): boolean => {
    return true
  }

  const getDefaultContent = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'budget':
        return getBudgetContent(campaigns, true)
    }
  }

  const getSectionDisplayName = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'budget':
        return 'Budget'
    }
  }

  const getSectionName = (sectionType: SectionType | null): string => {
    if (!sectionType) return 'Section'

    const section = mediaPlanSection?.[sectionType]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return title
    }

    return getSectionDisplayName(sectionType)
  }

  const getDeleteModalTitle = (): string => {
    return `Delete ${getSectionName(deletingSectionType)}`
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = mediaPlanSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = mediaPlanSection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const getEditFlyoutTitle = (): string => {
    return `Edit ${getSectionName(editingSectionType)}`
  }

  const captureGanttChart = async () => {
    const maxAttempts = 5
    let attempts = 0

    while (attempts < maxAttempts) {
      attempts++

      if (ganttChartRef.current) {
        try {
          const canvas = await html2canvas(ganttChartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            allowTaint: true
          })
          const imageData = canvas.toDataURL('image/png')

          dispatch(
            setGanttChartImage({
              planId: masterCampaignPlan.id,
              image: imageData,
              campaignsHash
            })
          )

          return imageData
        } catch (error) {
          console.error('Failed to capture gantt chart:', error)
          return undefined
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }
  }

  const captureMapImages = async () => {
    const mapImages: Record<string, string | null> = {}

    const mapReadinessPromises = campaigns
      .filter(campaign => campaign.zips && campaign.zips.length > 0)
      .map(async campaign => {
        const maxAttempts = 5
        let attempts = 0

        while (attempts < maxAttempts) {
          attempts++
          const mapRef = campaignMapRefs.current.get(campaign.id)

          if (mapRef) {
            return waitForMapReady(() => mapRef.isMapReady())
          } else {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        }

        throw new Error(`Map for campaign ${campaign.id} did not become ready in time`)
      })

    await Promise.all(mapReadinessPromises)

    await new Promise(resolve => setTimeout(resolve, 500))

    for (const campaign of campaigns) {
      if (campaign.zips && campaign.zips.length > 0) {
        const mapRef = campaignMapRefs.current.get(campaign.id)
        if (mapRef) {
          try {
            const mapImage = await mapRef.getMapCanvas()
            mapImages[campaign.id] = mapImage
          } catch (error) {
            console.error(`Failed to capture map for campaign ${campaign.id}:`, error)
            mapImages[campaign.id] = null
          }
        }
      }
    }

    return mapImages
  }

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)

      const ganttImage = await captureGanttChart()
      const mapImages = await captureMapImages()

      setCampaignMapImages(mapImages)

      const filename = `${masterCampaignPlan.name} - Media Plan.pdf`

      await downloadPdf(<MediaPlanPdf campaigns={campaigns} ganttChartImage={ganttImage} campaignMapImages={mapImages} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const onTransferToDspClick = () => {
    if (!currentOrganization?.id || !currentAccount?.id || !masterCampaignPlan?.id) {
      return
    }

    transferMasterCampaignPlanToDspMutation({
      organizationId: currentOrganization.id,
      accountId: currentAccount.id,
      masterCampaignPlanId: masterCampaignPlan.id
    })
  }

  useEffect(() => {
    if (!transferMasterCampaignPlanToDspResult.isSuccess || !transferMasterCampaignPlanToDspResult.data) {
      return
    }

    setTransferToDspModalVisible(false)
    history.push(`/masterCampaigns/${transferMasterCampaignPlanToDspResult.data.id}`)
  }, [transferMasterCampaignPlanToDspResult, history])

  const listCampaigns = [...campaigns]
    .sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
    .map(builder => {
      const calculatedEndDate = calculateEndDate(builder)
      return (
        <div key={builder.id} style={{ marginBottom: '1rem' }}>
          <CampaignCard builder={{ ...builder, endDate: calculatedEndDate }} onEditClick={editMode ? () => onEditCampaignClick?.(builder) : undefined} onRemoveCampaignLoading={isRemovingCampaign} onRemoveCampaignClick={editMode ? async () => onRemoveCampaignClick?.(builder) : undefined} masterCampaignPlanId={masterCampaignPlan.id} masterCampaignPlanStatus={masterCampaignPlan.status} showActions={editMode} />
        </div>
      )
    })

  const isLoading = useMemo(() => {
    return !masterCampaignStub || masterCampaignStub.id !== masterCampaignPlan.masterCampaignStubId || isMasterCampaignStubLoading
  }, [masterCampaignStub, masterCampaignPlan, isMasterCampaignStubLoading])

  if (isLoading) {
    return (
      <>
        <EuiTitle size='m'>
          <h3>Media Plan</h3>
        </EuiTitle>
        <EuiSpacer size='m' />
        <EuiLoadingContent lines={10} />
      </>
    )
  }

  return (
    <>
      <TabPageHeader title={mediaPlanSection?.pageTitle || 'Media Plan'} onClientPdfClick={handlePdfDownload} isGeneratingPdf={isGeneratingPdf} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} onEditPageTitle={editMode ? handleEditPageTitle : undefined} />

      {!budgetHidden && (
        <>
          <Budget campaigns={campaigns} useMediaPlanWording={true} section={mediaPlanSection?.budget} onEdit={() => handleEditSection('budget')} onDelete={() => handleDeleteSection('budget')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={mediaPlanSection?.aboveCampaignTimeline} position='aboveCampaignTimeline' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position='aboveCampaignTimeline' onClick={handleAddCustomSection} />
          <EuiSpacer size='xl' />
        </>
      )}

      <CampaignTimeline campaigns={campaigns} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isLoading={isLoading} isClickable={true} isHoverable={true} />

      {(isGeneratingPdf || isMultitabPdfGenerating) && (
        <div ref={ganttChartRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '1200px', overflow: 'visible' }}>
          <CampaignTimeline campaigns={campaigns} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isLoading={isLoading} ganttChartRef={ganttChartRef} isClickable={false} isHoverable={false} />
        </div>
      )}

      {campaigns.map(campaign => {
        if (!campaign.zips || campaign.zips.length === 0) {
          return null
        }

        const isHouseholdCampaign = campaign.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || campaign.campaignType === MasterCampaignPlanCampaignBuilderType.TV || (campaign.isOffAdCritter && campaign.includeInProjections && campaign.includeInGeoHeatmap)
        const isBillboardCampaign = campaign.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard

        if ((isGeneratingPdf || isMultitabPdfGenerating) && !isHouseholdCampaign && !isBillboardCampaign) {
          return null
        }

        return (
          <div key={`campaign-map-${campaign.id}`} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '800px', height: '600px', overflow: 'visible' }}>
            {isHouseholdCampaign && (
              <CampaignSpecificTargetedHouseholdMap
                ref={ref => {
                  if (ref) {
                    campaignMapRefs.current.set(campaign.id, ref)
                  } else {
                    campaignMapRefs.current.delete(campaign.id)
                  }
                }}
                masterCampaignPlan={masterCampaignPlan}
                campaign={campaign}
                isCapturing
              />
            )}
            {isBillboardCampaign && masterCampaignStub && (
              <CampaignSpecificBillboardMap
                ref={ref => {
                  if (ref) {
                    campaignMapRefs.current.set(campaign.id, ref)
                  } else {
                    campaignMapRefs.current.delete(campaign.id)
                  }
                }}
                masterCampaignStub={masterCampaignStub}
                campaign={campaign}
                isCapturing
              />
            )}
          </div>
        )
      })}

      <EuiSpacer size='xxl' />

      <RenderCustomSections sections={mediaPlanSection?.aboveCampaignDetails} position='aboveCampaignDetails' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position='aboveCampaignDetails' onClick={handleAddCustomSection} />
          <EuiSpacer size='m' />
        </>
      )}

      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiFlexGroup alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='s'>
                <h4>{mediaPlanSection?.campaignDetailsTitle || 'Campaign Details'}</h4>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <EuiFlexItem grow={false}>
                <EuiButtonIcon iconType='pencil' onClick={handleEditCampaignDetailsTitle} aria-label='Edit campaign details title' />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        {editMode && masterCampaignPlan?.status !== MasterCampaignPlanStatus.TransferredToDsp && (
          <EuiFlexItem grow={false}>
            <EuiButton size='s' onClick={() => onAddCampaignClick?.()}>
              Add Campaign
            </EuiButton>
          </EuiFlexItem>
        )}
        {masterCampaignPlan?.status === MasterCampaignPlanStatus.TransferredToDsp ? (
          <EuiFlexItem grow={false}>
            <EuiButton size='s' onClick={() => history.push(`/masterCampaigns/${masterCampaignPlan?.masterCampaignId}`)}>
              View Master Campaign in DSP
            </EuiButton>
          </EuiFlexItem>
        ) : (
          masterCampaignPlan?.status === MasterCampaignPlanStatus.Ready && (
            <EuiFlexItem grow={false}>
              <EuiButton size='s' color='primary' fill onClick={() => setTransferToDspModalVisible(true)}>
                Transfer All to DSP
              </EuiButton>
            </EuiFlexItem>
          )
        )}
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      <div style={{ width: '100%' }}>{listCampaigns}</div>

      {campaigns.length === 0 && (
        <EuiText>
          <p>No campaigns have been configured for this plan yet.</p>
        </EuiText>
      )}

      {editingSectionType && <EditCustomSectionFlyout section={mediaPlanSection?.[editingSectionType]} defaultTitle={getDefaultTitle(editingSectionType)} defaultContent={getDefaultContent(editingSectionType)} defaultShowTitle={getDefaultShowTitle(editingSectionType)} flyoutTitle={getEditFlyoutTitle()} onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} showBudgetWarning={editingSectionType === 'budget'} />}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? mediaPlanSection?.[editingCustomSection.position]?.[editingCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
        />
      )}

      {deletingSectionType && <AcConfirmModal message={'Are you sure you want to delete this section?'} title={getDeleteModalTitle()} onCancel={handleCancelDelete} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={handleCancelDeleteCustomSection} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {showBudgetSaveWarning && (
        <AcConfirmModal
          message={"Once you customize this budget section, it becomes static and won't automatically update with campaign changes. Future edits to campaign budgets may cause the dollar amount and percentages here to become out of sync."}
          title='Save Budget Section'
          onCancel={cancelBudgetSave}
          onConfirm={confirmBudgetSave}
          cancelButtonText='Cancel'
          confirmButtonText='Save'
          buttonColor='primary'
          isLoading={isUpdatingBudget}
          confirmButtonDisabled={isUpdatingBudget}
        />
      )}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={mediaPlanSection?.pageTitle || 'Media Plan'} defaultTitle='Media Plan' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}

      {editingCampaignDetailsTitle && <EditPageTitleFlyout currentTitle={mediaPlanSection?.campaignDetailsTitle || 'Campaign Details'} defaultTitle='Campaign Details' onSave={handleSaveCampaignDetailsTitle} onClose={() => setEditingCampaignDetailsTitle(false)} isLoading={isUpdatingCampaignDetailsTitle} />}

      {transferToDspModalVisible && masterCampaignPlan?.status !== MasterCampaignPlanStatus.TransferredToDsp && (
        <AgencyV2Modal isLoading={transferMasterCampaignPlanToDspResult.isLoading} title='Transfer to DSP' onClose={() => setTransferToDspModalVisible(false)} showCancelButton confirmButtonText='Transfer' onConfirmButtonClick={onTransferToDspClick} bodyHeight='11em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>This will copy all of the campaigns and documents from this plan into AdCritter DSP where you will have full editing control.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Are you ready to transfer this plan into the DSP?</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2Modal>
      )}
    </>
  )
})

export default MediaPlan
